import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle,
  Clock,
  Users,
  Play,
  Pause,
  CheckSquare,
  Square,
  GripVertical
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useSessionContext } from '../../contexts/SessionContext';
import { getTaskType, handleTaskAction } from '../../utils/taskTypeUtils';

const BatchTaskCard = ({
  batch,
  onToggleExpand,
  onCompleteAll,
  onCompleteTask,
  onStartSession,
  onDragStart,
  onDragEnd,
  reschedulingTaskId,
  showRescheduleMenu,
  onShowRescheduleMenu,
  onReschedule
}) => {
  const navigate = useNavigate();
  const [localExpanded, setLocalExpanded] = useState(batch.isExpanded || false);
  const { activeSession } = useSessionContext();

  // Check if this batch is currently in a session
  const isSessionActive = activeSession && activeSession.id === batch.id;
  // Check if another session is running
  const isOtherSessionActive = activeSession && activeSession.id !== batch.id;

  const isExpanded = localExpanded;
  const progressPercentage = batch.taskCount > 0
    ? Math.round((batch.completedCount / batch.taskCount) * 100)
    : 0;

  // Determine card styling based on state
  const getCardStyling = () => {
    if (reschedulingTaskId && batch.tasks.some(t => t.id === reschedulingTaskId)) {
      return 'bg-stone-50 border-stone-300 opacity-50 pointer-events-none';
    }
    // If this batch has an active session, highlight it
    if (isSessionActive) {
      return 'bg-teal-50 border-teal-400 ring-2 ring-teal-400 ring-opacity-50';
    }
    // If another session is running, gray this one out
    if (isOtherSessionActive) {
      return 'bg-stone-50 border-stone-200 opacity-50';
    }
    if (batch.completedCount === batch.taskCount) {
      return 'bg-stone-50 border-stone-200 opacity-50';
    }
    if (batch.priority === 'critical') {
      return 'bg-red-50 border-red-300';
    }
    if (batch.priority === 'high') {
      return 'bg-amber-50 border-amber-300';
    }
    return 'bg-white border-stone-200 hover:shadow-sm';
  };

  // Get category color for batch
  const getCategoryColor = () => {
    const categoryColors = {
      'profile': 'bg-pink-50 text-pink-800',
      'research': 'bg-azure-50 text-azure-800',
      'testing': 'bg-violet-50 text-violet-800',
      'essay': 'bg-amber-50 text-amber-800',
      'financial': 'bg-emerald-50 text-emerald-800',
      'admin': 'bg-violet-50 text-violet-800',
      'decision': 'bg-indigo-50 text-indigo-800'
    };
    return categoryColors[batch.category] || 'bg-stone-100 text-slate-800';
  };


  // Handle expand/collapse
  const handleToggleExpand = (e) => {
    e.stopPropagation();
    setLocalExpanded(!localExpanded);
    if (onToggleExpand) {
      onToggleExpand(batch.id);
    }
  };

  // Handle complete all
  const handleCompleteAll = (e) => {
    e.stopPropagation();
    if (onCompleteAll) {
      onCompleteAll(batch.id);
    }
  };

  // Handle start session
  const handleStartSession = (e) => {
    e.stopPropagation();
    if (onStartSession) {
      onStartSession(batch.id);
    }
  };

  return (
    <div
      onContextMenu={(e) => {
        e.preventDefault();
        if (batch.completedCount < batch.taskCount && onShowRescheduleMenu) {
          onShowRescheduleMenu(batch.id);
        }
      }}
      className={`
        border rounded-lg transition-all duration-200 group relative
        ${getCardStyling()}
      `}
    >
      {/* Main Card Content */}
      <div className="p-3">
        <div className="flex items-start gap-2">
          {/* Drag Handle */}
          {batch.completedCount < batch.taskCount && (
            <div
              draggable
              onDragStart={(e) => onDragStart && onDragStart(e, batch)}
              onDragEnd={onDragEnd}
              className="flex-shrink-0 p-1 hover:bg-stone-100 rounded cursor-move opacity-0 group-hover:opacity-100 transition-opacity"
              title="Drag to reschedule batch"
            >
              <GripVertical className="w-4 h-4 text-stone-400" />
            </div>
          )}

          {/* Expand/Collapse Button */}
          <button
            onClick={handleToggleExpand}
            className="flex-shrink-0 p-0.5 hover:bg-stone-50 rounded transition-colors mt-0.5"
          >
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-slate-800" />
            ) : (
              <ChevronRight className="w-5 h-5 text-slate-800" />
            )}
          </button>

          {/* Batch Info */}
          <div className="flex-1 min-w-0">
            {/* Title and Session Info */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                {/* Active Session Indicator */}
                {isSessionActive && (
                  <div className="relative">
                    <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                    <div className="absolute inset-0 w-2 h-2 bg-teal-400 rounded-full animate-ping"></div>
                  </div>
                )}
                <h4 className={`text-sm font-medium leading-5 ${
                  batch.completedCount === batch.taskCount
                    ? 'line-through text-slate-800'
                    : 'text-slate-900'
                }`}>
                  {batch.title}
                </h4>
              </div>

              {/* Action Button */}
              {batch.isSession && batch.completedCount === 0 && (
                <button
                  onClick={handleStartSession}
                  disabled={isOtherSessionActive}
                  className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors flex items-center gap-1 ${
                    isSessionActive
                      ? 'bg-teal-600 text-white'
                      : isOtherSessionActive
                      ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                      : 'bg-slate-900 text-white hover:bg-slate-700'
                  }`}
                >
                  {isSessionActive ? (
                    <>
                      <Pause className="w-3 h-3" />
                      Session Active
                    </>
                  ) : (
                    <>
                      <Play className="w-3 h-3" />
                      Start Session
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Meta Information */}
            <div className="flex items-center gap-3 mt-2">
              {/* Progress */}
              <div className="flex items-center gap-2">
                <div className="w-20 h-1.5 bg-stone-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <span className="text-xs text-slate-800">
                  {batch.completedCount}/{batch.taskCount}
                </span>
              </div>

              {/* Category */}
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor()}`}>
                {batch.category}
              </span>


              {/* Time Estimate */}
              <span className="text-xs text-slate-800 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {batch.estimated_hours.toFixed(1)}h total
              </span>

              {/* College Count */}
              {batch.colleges.length > 0 && (
                <span className="text-xs text-slate-800 flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {batch.colleges.length} colleges
                </span>
              )}

              {/* Priority Indicator - red dot for critical priority */}
              {batch.priority === 'critical' && (
                <span className="w-2 h-2 bg-red-500 rounded-full" />
              )}
            </div>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="mt-4 ml-7">
            {/* Quick Actions */}
            {batch.completedCount < batch.taskCount && (
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={handleCompleteAll}
                  className="px-3 py-1 bg-emerald-600 text-white text-xs font-medium rounded hover:bg-emerald-700 transition-colors flex items-center gap-1"
                >
                  <CheckSquare className="w-3 h-3" />
                  Complete All Remaining
                </button>
                <span className="text-xs text-slate-800">
                  or work individually below
                </span>
              </div>
            )}

            {/* Individual Tasks */}
            <div className="space-y-2">
              {batch.tasks.map((task, index) => (
                <div
                  key={task.id}
                  className={`
                    flex items-start gap-2 p-2 rounded border transition-all group
                    ${task.isCompleted
                      ? 'bg-stone-50 border-stone-200 opacity-50'
                      : 'bg-white border-stone-200 hover:border-stone-300'
                    }
                  `}
                >
                  {/* Task Checkbox */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCompleteTask && onCompleteTask(task.id);
                    }}
                    className="flex-shrink-0 p-0.5 hover:bg-stone-50 rounded transition-colors"
                  >
                    {task.isCompleted ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <Circle className="w-4 h-4 text-stone-400" />
                    )}
                  </button>

                  {/* Task Info */}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${task.isCompleted ? 'line-through text-slate-800' : 'text-slate-900'}`}>
                      {task.title}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {task.college_name && (
                        <span className="text-xs text-slate-800">
                          {task.college_name}
                        </span>
                      )}
                      <span className="text-xs text-slate-800">
                        {task.estimated_hours || 0.5}h
                      </span>
                    </div>
                  </div>

                  {/* Action Button */}
                  {!task.isCompleted && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const taskType = getTaskType(task);
                        handleTaskAction(task, navigate);
                      }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 text-xs font-medium text-white rounded bg-azure-500 hover:bg-azure-600 flex-shrink-0"
                      title={getTaskType(task).actionText}
                    >
                      {getTaskType(task).actionText}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Session Tips */}
            {batch.isSession && (
              <div className="mt-3 p-3 bg-azure-50 border border-azure-200 rounded-lg">
                <p className="text-xs text-azure-800">
                  💡 <strong>Tip:</strong> Work through these colleges in one focused session for efficiency.
                  {batch.batchType === 'RESEARCH' && ' Take notes on what makes each school unique.'}
                  {batch.batchType === 'VALUE_PROPS' && ' Focus on why you fit at each school specifically.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reschedule menu */}
      {showRescheduleMenu === batch.id && onReschedule && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg z-50 py-1 min-w-[160px]">
          <button
            onClick={() => onReschedule(batch, new Date())}
            className="w-full px-3 py-2 text-left text-sm font-medium hover:bg-stone-50"
          >
            Move to Today
          </button>
          <button
            onClick={() => {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              onReschedule(batch, tomorrow);
            }}
            className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50"
          >
            Move to Tomorrow
          </button>
          <button
            onClick={() => {
              const nextWeek = new Date();
              nextWeek.setDate(nextWeek.getDate() + 7);
              onReschedule(batch, nextWeek);
            }}
            className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50"
          >
            Move to Next Week
          </button>
          <button
            onClick={() => {
              const nextWeekend = new Date();
              nextWeekend.setDate(nextWeekend.getDate() + (6 - nextWeekend.getDay()));
              onReschedule(batch, nextWeekend);
            }}
            className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50"
          >
            Move to Weekend
          </button>
          <hr className="my-1" />
          <button
            onClick={() => onShowRescheduleMenu(null)}
            className="w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-stone-50"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default BatchTaskCard;